import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Sprout, UserPlus, Trash2, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", farm_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Compte créé.");
      setCreateOpen(false);
      setForm({ email: "", password: "", farm_name: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Supprimer le compte ${u.email} et toutes ses parcelles ?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Compte supprimé.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleReset = async () => {
    try {
      await api.put(`/admin/users/${resetFor.id}/password`, { password: newPassword });
      toast.success("Mot de passe réinitialisé.");
      setResetFor(null);
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-stone-100">
      <header className="flex h-14 items-center justify-between bg-[#1a4731] px-4 text-white">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#e8c872] text-[#1a4731]">
            <Sprout size={20} />
          </span>
          <div className="text-base font-semibold">Administration — Champ Cadastre</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate("/")} data-testid="back-to-map">
            <ArrowLeft size={16} className="mr-2" /> Carte
          </Button>
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => { logout(); navigate("/login"); }} data-testid="admin-logout">
            Déconnexion
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-800">Utilisateurs</h1>
            <p className="text-sm text-stone-500">{users.length} compte(s) — connecté en tant que {user?.email}</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1a4731] text-white hover:bg-[#143726]" data-testid="open-create-user">
                <UserPlus size={16} className="mr-2" /> Nouveau compte
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Créer un compte agriculteur</DialogTitle>
                <DialogDescription>Le compte recevra ces identifiants pour se connecter.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4" data-testid="create-user-form">
                <div>
                  <Label htmlFor="cu-email">Email</Label>
                  <Input id="cu-email" type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1.5" data-testid="create-user-email" />
                </div>
                <div>
                  <Label htmlFor="cu-farm">Nom de l'exploitation</Label>
                  <Input id="cu-farm" value={form.farm_name}
                    onChange={(e) => setForm({ ...form, farm_name: e.target.value })}
                    placeholder="EARL Dupont" className="mt-1.5" data-testid="create-user-farm" />
                </div>
                <div>
                  <Label htmlFor="cu-pass">Mot de passe (min. 6 caractères)</Label>
                  <Input id="cu-pass" type="text" required minLength={6} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="mt-1.5" data-testid="create-user-password" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={submitting} className="bg-[#1a4731] text-white hover:bg-[#143726]" data-testid="submit-create-user">
                    {submitting && <Loader2 size={16} className="mr-2 animate-spin" />} Créer
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          {loading ? (
            <div className="p-10 text-center text-stone-400">Chargement…</div>
          ) : (
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Exploitation</th>
                  <th className="px-4 py-3">Rôle</th>
                  <th className="px-4 py-3">Parcelles</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium text-stone-800">{u.email}</td>
                    <td className="px-4 py-3 text-stone-600">{u.farm_name}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${u.role === "admin" ? "bg-[#e8c872] text-[#7a5e10]" : "bg-stone-100 text-stone-600"}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600">{u.parcel_count ?? 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" title="Réinitialiser le mot de passe"
                          onClick={() => { setResetFor(u); setNewPassword(""); }} data-testid={`reset-pw-${u.id}`}>
                          <KeyRound size={16} />
                        </Button>
                        {u.role !== "admin" && (
                          <Button variant="ghost" size="icon" title="Supprimer" className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(u)} data-testid={`delete-user-${u.id}`}>
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
            <DialogDescription>Définissez un nouveau mot de passe pour ce compte.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-stone-500">{resetFor?.email}</p>
          <Input type="text" minLength={6} placeholder="Nouveau mot de passe" value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)} data-testid="reset-pw-input" />
          <DialogFooter>
            <Button onClick={handleReset} disabled={newPassword.length < 6} className="bg-[#1a4731] text-white hover:bg-[#143726]" data-testid="submit-reset-pw">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
